import { AppMessagePlatform, IAppMessage } from '@typings';
import { MessageType } from '@utils/enums';
import Airtable from 'airtable';

/** Upper bounds on editor-supplied text so a bad row cannot fill the viewport. */
export const APP_MESSAGE_TITLE_MAX_LENGTH = 200;
export const APP_MESSAGE_TEXT_MAX_LENGTH = 1000;
/** `Meta` is parsed, so bound the raw string too — Airtable long text allows ~100,000 characters. */
export const APP_MESSAGE_META_MAX_LENGTH = 4000;

/** This app's platform. Mobile's port of this mapper uses `MOBILE`. */
export const PLATFORM: AppMessagePlatform = 'WEB';

/**
 * Separator between the platform prefix and the route key in an Airtable `Routes` option:
 * `web → /profile`. U+2192 RIGHTWARDS ARROW with a single space either side, exactly as the
 * option list is created by `scripts/airtable/create-app-messages-table.mjs`.
 */
export const ROUTE_PREFIX_SEPARATOR = ' → ';

/**
 * Defensive normalisation: `Forms` should already hold GraphQL codes (`MSAC1`, as in `consignment.forms[].type`),
 * but an editor typing the API notation `MSA.C.1` still matches (see the matcher's form check).
 */
export const stripFormCode = (code: string): string => code.replace(/\./g, '');

const readText = (value: unknown, maxLength: number): string | null => {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, maxLength) : null;
};

const readStringList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && item.trim() !== '') : [];

/** Airtable date fields return `YYYY-MM-DD`; a field created with a time component returns an ISO timestamp. */
const readDateOnly = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, 10) : null;
};

const readLevel = (value: unknown): MessageType => {
    const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
    return (Object.values(MessageType) as string[]).includes(candidate) ? (candidate as MessageType) : MessageType.Info;
};

const readMeta = (value: unknown): Record<string, unknown> | null => {
    if (typeof value !== 'string' || !value.trim()) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(value.trim().slice(0, APP_MESSAGE_META_MAX_LENGTH));
        return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : null;
    } catch {
        return null;
    }
};

const readNumber = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/**
 * `Routes` options for one platform, with the prefix stripped: `web → /profile` becomes `/profile`.
 *
 * Filters **option by option, never row by row**. The seed row carries web and mobile options in
 * one cell, so discarding a whole row because it mentions the other platform would hide it on both.
 * A row left with nothing simply gets `[]`, which "blank means never" already handles.
 *
 * Anything not carrying this platform's exact prefix is dropped, so a mistyped or unprefixed option
 * can never match — it cannot silently behave like a wildcard.
 */
export const readRoutesForPlatform = (value: unknown, platform: AppMessagePlatform): string[] => {
    const prefix = `${platform.toLowerCase()}${ROUTE_PREFIX_SEPARATOR}`;
    return readStringList(value)
        .map((option) => option.trim())
        .filter((option) => option.startsWith(prefix))
        .map((option) => option.slice(prefix.length).trim())
        .filter((key) => key !== '');
};

/**
 * Maps one Airtable `App_Messages` record to an `IAppMessage`. Returns null when the row has no
 * Key or no message, so it can never render. Field names must match the runbook exactly.
 */
export const mapAppMessageRecord = (record: Airtable.Record<any>): IAppMessage | null => {
    const key = readText(record.get('Key'), APP_MESSAGE_TITLE_MAX_LENGTH);
    const message = readText(record.get('Message'), APP_MESSAGE_TEXT_MAX_LENGTH);

    if (!key || !message) {
        return null;
    }

    return {
        id: record.id,
        key,
        level: readLevel(record.get('Level')),
        title: readText(record.get('Title'), APP_MESSAGE_TITLE_MAX_LENGTH),
        message,
        platforms: readStringList(record.get('Platform')) as AppMessagePlatform[],
        routes: readRoutesForPlatform(record.get('Routes'), PLATFORM),
        forms: readStringList(record.get('Forms')).map(stripFormCode),
        statuses: readStringList(record.get('Show_For_Status')),
        active: record.get('Active') === true,
        order: readNumber(record.get('Order')),
        startDate: readDateOnly(record.get('Start_Date')),
        endDate: readDateOnly(record.get('End_Date')),
        meta: readMeta(record.get('Meta')),
    };
};
